// ============================================================================
// CALENDAR DATE HELPERS — day-keyed values, no timezone drift
// ============================================================================
// Every screen that keys on a CALENDAR DAY (occupancy strip, day view, month
// calendar) must agree on two things, or dates silently shift by one:
//
//  1. A day key is `yyyy-MM-dd`. The API sends C# `DateTime`, which serialises
//     as `"2026-08-17T00:00:00"` — appending another `'T00:00:00'` to that
//     produces an INVALID date, which is exactly how the strip came to render
//     `NaN` and the Day View "Invalid Date". `dayKey()` accepts either form.
//
//  2. Parsing must be LOCAL, not UTC. `new Date('2026-08-17')` is parsed as
//     UTC midnight, so east of Greenwich `getDate()` can return the 16th.
//     `parseDayKey()` builds the date from its parts instead.
//
// Likewise never use `toISOString().slice(0,10)` to derive today's key — that
// converts to UTC first and rolls over at the wrong moment.

/** Normalises an API date (`yyyy-MM-dd` or a full ISO datetime) to a day key. */
export const dayKey = (value: string | null | undefined): string =>
  (value ?? '').slice(0, 10);

/** Local-midnight Date for a day key. Safe for getDay()/getDate()/toLocaleDateString(). */
export const parseDayKey = (key: string): Date => {
  const [y, m, d] = dayKey(key).split('-').map(Number);
  return new Date(y, (m || 1) - 1, d || 1);
};

/** Day key for a Date, in LOCAL time. */
export const toDayKey = (d: Date): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

/** Today's day key, local. */
export const todayKey = (): string => toDayKey(new Date());

/** Day key shifted by whole days. */
export const addDays = (key: string, days: number): string => {
  const d = parseDayKey(key);
  d.setDate(d.getDate() + days);
  return toDayKey(d);
};

/** True when the key is a real date. Guards against a bad API value reaching the UI. */
export const isValidDayKey = (key: string | null | undefined): boolean => {
  const k = dayKey(key);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(k)) return false;
  return !Number.isNaN(parseDayKey(k).getTime());
};
